/**
 * AnalysisPage — Analysis stage container with tab system
 *
 * Phase 4.4 — redesign-p4-analatt-44b
 * 6 panels: Attorney Analysis, Adjuster Analysis, Monte Carlo, War Room, Co-pilot, Engine Audit
 * Hash-based deep-links: #attorney-analysis, #adjuster-analysis, #monte-carlo, #war-room, #copilot, #engine-audit
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Scale, Shield, BarChart3, MessagesSquare, Sparkles, BarChart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BRAND_NAME } from '@/config/brand';

// Import tab components
import { AttorneyAnalysisTab } from './AttorneyAnalysisTab';
import { AdjusterAnalysisTab } from './AdjusterAnalysisTab';
import { MonteCarloTab } from './MonteCarloTab';
import { WarRoomPanel } from './WarRoomPanel';
import { CopilotPanel } from './CopilotPanel';
import { EngineAuditPanel } from './EngineAuditPanel';

type AnalysisTab =
  | 'attorney-analysis'
  | 'adjuster-analysis'
  | 'monte-carlo'
  | 'war-room'
  | 'copilot'
  | 'engine-audit';

interface TabConfig {
  id: AnalysisTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  component: React.ComponentType<{ reportId: string }>;
}

const TABS: TabConfig[] = [
  {
    id: 'attorney-analysis',
    label: 'Attorney Analysis',
    icon: Scale,
    component: AttorneyAnalysisTab,
  },
  {
    id: 'adjuster-analysis',
    label: 'Adjuster Analysis',
    icon: Shield,
    component: AdjusterAnalysisTab,
  },
  {
    id: 'monte-carlo',
    label: 'Monte Carlo',
    icon: BarChart3,
    component: MonteCarloTab,
  },
  {
    id: 'war-room',
    label: 'War Room',
    icon: MessagesSquare,
    component: WarRoomPanel,
  },
  {
    id: 'copilot',
    label: 'Co-pilot',
    icon: Sparkles,
    component: CopilotPanel,
  },
  {
    id: 'engine-audit',
    label: 'Engine Audit',
    icon: BarChart,
    component: EngineAuditPanel,
  },
];

export function AnalysisPage() {
  const { reportId, caseId } = useParams<{
    reportId?: string;
    caseId?: string;
    '*': string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Get initial tab from hash or default to attorney-analysis
  const getInitialTab = (): AnalysisTab => {
    const hash = location.hash.replace('#', '');
    if (hash && TABS.some((t) => t.id === hash)) {
      return hash as AnalysisTab;
    }
    // Check if there's a sub-route
    const subRoute = location.pathname.split('/').pop();
    if (subRoute && TABS.some((t) => t.id === subRoute)) {
      return subRoute as AnalysisTab;
    }
    return 'attorney-analysis';
  };

  const [activeTab, setActiveTab] = useState<AnalysisTab>(getInitialTab());

  // Sync hash with active tab
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && TABS.some((t) => t.id === hash) && hash !== activeTab) {
      setActiveTab(hash as AnalysisTab);
    }
  }, [location.hash]);

  // Update hash when tab changes
  const handleTabChange = (tabId: AnalysisTab) => {
    setActiveTab(tabId);
    // Update URL hash without navigation
    window.history.replaceState(null, '', `${location.pathname}#${tabId}`);
  };

  // Get the active tab component
  const ActiveComponent = TABS.find((t) => t.id === activeTab)?.component || AttorneyAnalysisTab;

  // Use caseId if reportId not available (for attorney routes)
  const effectiveReportId = reportId || caseId || '';

  if (!effectiveReportId) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <p className="text-[var(--color-text-secondary)]">No case selected.</p>
          <p className="mt-2 text-sm text-[var(--color-text-muted)]">Open a case to view analysis.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-base)]">
      {/* Header */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-bg-surface)]">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-2xl font-semibold text-[var(--color-text-primary)]"
                style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
              >
                Analysis
              </h1>
              <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                Case: <span className="font-mono">{effectiveReportId}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-bg-surface)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={cn(
                    'group inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors',
                    isActive
                      ? 'border-[var(--color-brand)] text-[var(--color-brand)]'
                      : 'border-transparent text-[var(--color-text-muted)] hover:border-[var(--color-text-dim)] hover:text-[var(--color-text-secondary)]'
                  )}
                  data-testid={`analysis-tab-${tab.id}`}
                >
                  <Icon
                    className={cn(
                      'h-4 w-4',
                      isActive ? 'text-[var(--color-brand)]' : 'text-[var(--color-text-muted)]'
                    )}
                  />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <ActiveComponent reportId={effectiveReportId} />
      </div>
    </div>
  );
}

export default AnalysisPage;
